import { message } from 'antd';
import { Demo, DemoDetails, DemoListResponse, DemoStats, DemoStatus } from '../../libs/types/src';
import { videoDescriptionService } from '../../services/apiServices/videoDescriptionService';
import { uploadWithProgress } from '../../utils/uploadWithProgress';

export const GET_DEMOS_REQUEST = 'GET_DEMOS_REQUEST';
export const GET_DEMOS_SUCCESS = 'GET_DEMOS_SUCCESS';
export const GET_DEMOS_FAILURE = 'GET_DEMOS_FAILURE';
export const GET_DEMO_DETAILS_REQUEST = 'GET_DEMO_DETAILS_REQUEST';
export const GET_DEMO_DETAILS_SUCCESS = 'GET_DEMO_DETAILS_SUCCESS';
export const GET_DEMO_DETAILS_FAILURE = 'GET_DEMO_DETAILS_FAILURE';
export const UPLOAD_DEMO_REQUEST = 'UPLOAD_DEMO_REQUEST';
export const UPLOAD_DEMO_SUCCESS = 'UPLOAD_DEMO_SUCCESS';
export const UPLOAD_DEMO_FAILURE = 'UPLOAD_DEMO_FAILURE';
export const TOGGLE_DEMO_MODAL = 'TOGGLE_DEMO_MODAL';
export const SET_SELECTED_DEMO = 'SET_SELECTED_DEMO';
export const UPDATE_ADDITIONAL_CONTEXT_REQUEST = 'UPDATE_ADDITIONAL_CONTEXT_REQUEST';
export const UPDATE_ADDITIONAL_CONTEXT_SUCCESS = 'UPDATE_ADDITIONAL_CONTEXT_SUCCESS';
export const UPDATE_ADDITIONAL_CONTEXT_FAILURE = 'UPDATE_ADDITIONAL_CONTEXT_FAILURE';
export const GET_STATS_REQUEST = 'GET_STATS_REQUEST';
export const GET_STATS_SUCCESS = 'GET_STATS_SUCCESS';
export const GET_STATS_FAILURE = 'GET_STATS_FAILURE';
export const REMOVE_DEMO_REQUEST = 'REMOVE_DEMO_REQUEST';
export const REMOVE_DEMO_SUCCESS = 'REMOVE_DEMO_SUCCESS';
export const REMOVE_DEMO_FAILURE = 'REMOVE_DEMO_FAILURE';
export const UPDATE_CURRENT_STATUS_SUCCESS = 'UPDATE_CURRENT_STATUS_SUCCESS';
export const SYNC_DEMO_STATUS_REQUEST = 'SYNC_DEMO_STATUS_REQUEST';
export const SYNC_DEMO_STATUS_SUCCESS = 'SYNC_DEMO_STATUS_SUCCESS';
export const SYNC_DEMO_STATUS_FAILURE = 'SYNC_DEMO_STATUS_FAILURE';
export const UPLOAD_DEMO_PROGRESS = 'UPLOAD_DEMO_PROGRESS';

export const demoActions = {
  getDemos,
  getDemoDetails,
  uploadDemo,
  toggleDemoModal,
  setSelectedDemo,
  updateAdditionalContext,
  getStats,
  removeDemo,
  updateCurrentStatus,
  syncDemoStatus,
};

function getDemos(page = 1, pageSize = 10) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await videoDescriptionService.getListOfVideos({ page: page, page_size: pageSize });
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to fetch demos'));
    }
  };

  function request() {
    return { type: GET_DEMOS_REQUEST };
  }
  function success(demos: DemoListResponse | Demo[]) {
    return { type: GET_DEMOS_SUCCESS, demos };
  }
  function failure(error: string) {
    return { type: GET_DEMOS_FAILURE, error };
  }
}

function syncDemoStatus() {
  return async (dispatch: any, getState: any) => {
    const demos = getState().demos.list;
    const ids = demos.filter((demo: Demo) => ![DemoStatus.PROCESSED, DemoStatus.FAILED].includes(demo.status)).map((demo: Demo) => demo.id);
    dispatch(request());
    try {
      if (ids.length === 0) return;
      const result = await videoDescriptionService.getListOfVideos({ id__in: ids.join(',') });
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to sync demo status'));
    }
  };
  function request() {
    return { type: SYNC_DEMO_STATUS_REQUEST };
  }
  function success(response: any) {
    return { type: SYNC_DEMO_STATUS_SUCCESS, response };
  }
  function failure(error: string) {
    return { type: SYNC_DEMO_STATUS_FAILURE, error };
  }
}

function getDemoDetails(videoId: string) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await videoDescriptionService.getVideoDescription(videoId);
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to fetch demo details'));
    }
  };

  function request() {
    return { type: GET_DEMO_DETAILS_REQUEST };
  }
  function success(demoDetails: DemoDetails) {
    return { type: GET_DEMO_DETAILS_SUCCESS, demoDetails };
  }
  function failure(error: string) {
    return { type: GET_DEMO_DETAILS_FAILURE, error };
  }
}

function uploadDemo(file: File) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const { data: { presigned_url, key } } = await videoDescriptionService.getUploadUrl({ file_name: file.name, file_type: file.type });

      await uploadWithProgress(presigned_url, file, (percent) => {
        dispatch(uploadProgress(percent));
      });
      const result = await videoDescriptionService.createVideoDescription({ attachment: key, file_name: file.name });
      message.success("Video uploaded successfully");
      dispatch(success(result.data));
      dispatch(uploadProgress(0));
    } catch (e) {
      message.error("Failed to upload video");
      dispatch(failure(e.message || 'Failed to upload video'));
    }
  };

  function uploadProgress(percent: number) {
    return { type: UPLOAD_DEMO_PROGRESS, percent };
  }

  function request() {
    return { type: UPLOAD_DEMO_REQUEST };
  }
  function success(demo: Demo) {
    return { type: UPLOAD_DEMO_SUCCESS, demo };
  }
  function failure(error: string) {
    return { type: UPLOAD_DEMO_FAILURE, error };
  }
}

function toggleDemoModal(show: boolean) {
  return (dispatch: any) => {
    dispatch({ type: TOGGLE_DEMO_MODAL, show });
  };
}

function setSelectedDemo(demo: Demo | null) {
  return (dispatch: any) => {
    dispatch({ type: SET_SELECTED_DEMO, demo });
  };
}

function updateAdditionalContext(videoId: string, content: string) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await videoDescriptionService.updateAdditionalContext(videoId, content);
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to update additional context'));
    }
  };

  function request() {
    return { type: UPDATE_ADDITIONAL_CONTEXT_REQUEST };
  }
  function success(demo: Demo) {
    return { type: UPDATE_ADDITIONAL_CONTEXT_SUCCESS, demo };
  }
  function failure(error: string) {
    return { type: UPDATE_ADDITIONAL_CONTEXT_FAILURE, error };
  }
}

function removeDemo(videoId: string) {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await videoDescriptionService.removeVideo(videoId);
      message.success("Video removed successfully");
      dispatch(success(videoId));
    } catch (e) {
      message.error("Failed to remove video");
      dispatch(failure(e.message || 'Failed to remove demo'));
    }
  };

  function request() {
    return { type: REMOVE_DEMO_REQUEST };
  }
  function success(videoId: string) {
    return { type: REMOVE_DEMO_SUCCESS, videoId };
  }
  function failure(error: string) {
    return { type: REMOVE_DEMO_FAILURE, error };
  }
}

function getStats() {
  return async (dispatch: any) => {
    dispatch(request());
    try {
      const result = await videoDescriptionService.getStats();
      dispatch(success(result.data));
    } catch (e) {
      dispatch(failure(e.message || 'Failed to fetch stats'));
    }
  };

  function request() {
    return { type: GET_STATS_REQUEST };
  }
  function success(stats: DemoStats) {
    return { type: GET_STATS_SUCCESS, stats };
  }
  function failure(error: string) {
    return { type: GET_STATS_FAILURE, error };
  }
}

function updateCurrentStatus(videoId: string) {
  return async (dispatch: any) => {
    try {
      const result = await videoDescriptionService.getCurrentStatus(videoId);
      dispatch(success(videoId, result.data.status));
    } catch (e) {
      console.log(e);
    }
  };


  function success(id: string, status: string) {
    return { type: UPDATE_CURRENT_STATUS_SUCCESS, id, status };
  }
} 